import SettingNotificationFilter from '@/pages/Settings/Notifications/components/SettingNotificationFilter';
import { Col, Flex, Row, Tooltip } from 'antd';
import React from 'react';
import AddButton from './AddButton';
import CustomParagraph from './CustomParagraph';
import CustomTitle from './CustomTitle';
import SearchInput from './SearchInput';
import ToggleSwitch from './ToggleSwitch';

interface PageHeaderActionsProps {
  title?: string;
  onToggleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  toggleChecked?: boolean;
  showToggle?: boolean;
  addButtonWidth?: number;
  toggleText?: string;
  toggleTooltip?: string;
  totalCount?: number | null;
  rangeStart?: number;
  rangeEnd?: number | null;
  showCount?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  onSearchClear?: () => void;
  showAddButton?: boolean;
  addButtonText?: string;
  onAddClick?: () => void;
  selectedItem?: string;
  setSelectedItem?: React.Dispatch<React.SetStateAction<string | ''>>;
  addButtonWrapper?: React.ReactNode;
  /** Rendered directly after the add button, e.g. a page-level overflow menu. */
  extraActions?: React.ReactNode;
}
/**
 * Page header actions component with search, toggle, count display and add button
 *
 * @param title - Page title to display
 * @param onToggleChange - Callback when toggle switch is changed (receives change event)
 * @param toggleChecked - Current state of toggle switch
 * @param showToggle - Whether to show the toggle switch
 * @param addButtonWidth - Width of the add button
 * @param toggleText - Text label for toggle switch
 * @param toggleTooltip - Tooltip text for toggle switch
 * @param totalCount - Total number of items
 * @param rangeStart - Start of current range
 * @param rangeEnd - End of current range
 * @param showCount - Whether to show count information
 * @param showSearch - Whether to show search input
 * @param searchPlaceholder - Placeholder text for search input
 * @param onSearchChange - Callback when search value changes
 * @param onSearchClear - Callback when search is cleared
 * @param showAddButton - Whether to show add button
 * @param addButtonText - Text for add button
 * @param onAddClick - Callback when add button is clicked
 */
const PageHeaderActions: React.FC<PageHeaderActionsProps> = ({
  title = '',
  onToggleChange,
  toggleChecked = false,
  showToggle = false,
  addButtonWidth,
  toggleText,
  toggleTooltip,
  totalCount = null,
  rangeStart = 1,
  rangeEnd = null,
  showCount = true,
  showSearch = true,
  searchPlaceholder = 'Search...',
  onSearchChange,
  onSearchClear,
  showAddButton = false,
  addButtonText = 'Add',
  onAddClick,
  selectedItem,
  setSelectedItem,
  addButtonWrapper,
  extraActions,
}) => {
  const getRangeText = (
    rangeStart: number,
    rangeEnd: number | null,
    totalCount: number
  ): string => {
    const hasStart = rangeStart !== null && rangeStart !== undefined;
    const hasEnd = rangeEnd !== null && rangeEnd !== undefined;

    if (hasStart && hasEnd) {
      if (rangeStart === 0 && rangeEnd === 0) {
        return `Showing 0 of ${totalCount}`;
      }
      return `Showing ${rangeStart}-${rangeEnd} of ${totalCount}`;
    } else if (hasStart) {
      return `Showing ${rangeStart} of ${totalCount}`;
    } else if (hasEnd) {
      return `Showing ${rangeEnd} of ${totalCount}`;
    } else {
      return `Showing 0 of ${totalCount}`;
    }
  };

  const addBtn = (
    <AddButton
      addButtonWidth={addButtonWidth}
      fontSize='12px'
      fontWeight='600'
      text={addButtonText}
      onClick={onAddClick}
    />
  );

  return (
    <div className='settings-header-wrapper'>
      <CustomTitle
        text={title}
        fontSize={20}
        fontWeight='700'
        lineHeight='28px'
        color='var(--text-primary)'
        marginBottom={4}
      />

      <Row>
        <Col
          className='s-left-col'
          xxl={12}
          xl={12}
          lg={10}
          md={10}
          sm={24}
          xs={24}
        >
          {showCount && totalCount !== null ? (
            <Flex align='center' gap={8}>
              <div className='s-count-wrapper'>
                {getRangeText(rangeStart, rangeEnd, totalCount)}
              </div>
              {title === 'Notifications' && (
                <SettingNotificationFilter
                  selectedItem={selectedItem}
                  setSelectedItem={setSelectedItem}
                />
              )}
            </Flex>
          ) : (
            <div />
          )}
        </Col>

        <Col
          className='s-right-col'
          xxl={12}
          xl={12}
          lg={14}
          md={14}
          sm={24}
          xs={24}
        >
          {showToggle && (
            <Flex align='center' gap={8}>
              <Tooltip title={toggleTooltip}>
                <span>
                  <CustomParagraph
                    text={toggleText}
                    fontSize='12px'
                    fontWeight='400'
                    lineHeight='20px'
                    color='var(--text-primary)'
                    marginBottom={0}
                    whiteSpace={'nowrap'}
                  />
                </span>
              </Tooltip>
              <ToggleSwitch checked={toggleChecked} onChange={onToggleChange} />
            </Flex>
          )}

          {showSearch && (
            <SearchInput
              width='200px'
              placeholder={searchPlaceholder}
              onChange={onSearchChange}
              onClear={onSearchClear}
            />
          )}

          {showAddButton && (addButtonWrapper ? addButtonWrapper : addBtn)}
          {extraActions}
        </Col>
      </Row>
    </div>
  );
};

export default PageHeaderActions;
